<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore'
import { useOrderStore } from '@/stores/OrderStore';
import { useToast } from 'vue-toastification';
import InputText from 'primevue/inputtext';

import FloatLabel from 'primevue/floatlabel';

const commentText = ref(null);


import axios from 'axios';

const toast = useToast();
const authStore = useAuthStore();
const orderStore = useOrderStore(); 

const orderId = ref('');
const invoiceData = ref({});
const isLoading = ref(false);
const error = ref(null);
const isSearchSubmitted = ref(false);

const handleKeyDown = (e) => {
  if (e.key === 'Enter') {
    fetchInvoice();
  }
};

// Fetch invoice data
const fetchInvoice = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const rawInput = orderId.value.trim();
    const trimmedOrderId = rawInput.startsWith('SAL-ORD-') ? rawInput.slice(8) : rawInput;

    const fullOrderNumber = `SAL-ORD-${trimmedOrderId}`;
    orderId.value = trimmedOrderId;
    console.log('Loading order', fullOrderNumber);

    const response = await axios.get(
      `/api/Order/GetInvoice?orderId=${encodeURIComponent(fullOrderNumber)}`,
      {
        headers: {
          'Authorization': `Bearer ${authStore.managerToken ? authStore.managerToken : authStore.token}`,
          'store-id': authStore.branchId
        }
      }
    );

    if (response.data.Status === 200 && (response.data.Product?.Invoice?.Status === 'Paid' || response.data.Product?.Invoice?.Status === 'Credit Note Issued')) {
      toast.success('Invoice loaded successfully');
      invoiceData.value = response.data.Product;
      isSearchSubmitted.value = true;
      console.log('Refund Invoice data', response.data);
      console.log('Invoice data invoice', invoiceData.value);

      invoiceData.value.Invoice?.OrderItems?.forEach((item, index) => {
        if (item.Sku === '2') {
          item.uniqueId = `2-${index}-${Date.now()}`;
        } else {
          item.uniqueId = item.Sku; 
        }
      });
      console.log('Invoice data after adding unique id', invoiceData.value);
    } else {
      toast.error(response.data.Product?.Message || 'Failed to fetch invoice');
      orderId.value = '';
      throw new Error(response.data.Message || 'Failed to fetch invoice');
    }
  } catch (err) {
    error.value = err.response?.data?.Product?.Message || err.message;
    console.error('API Error:', err);
  } finally {
    isLoading.value = false;
  }
  getTaxRate();

};

const originalQuantities = ref({});
const returnQuantities = ref({});

watch(() => invoiceData.value.Invoice?.OrderItems, (items) => {
  if (items) {
    originalQuantities.value = {};
    returnQuantities.value = {};
    items.forEach(item => {
      const returnedQty = getReturnedQty(item);
      originalQuantities.value[item.uniqueId] = item.Qty - returnedQty;
      returnQuantities.value[item.uniqueId] = item.Qty - returnedQty;
    });
  }
}, { immediate: true });

// Check if item has been returned
const alredyReturnedItems = (item) => {
  if (!invoiceData.value.Returns || invoiceData.value.Returns.length === 0) return true;

  let totalReturnedQty = 0;
  invoiceData.value.Returns.forEach(returnRecord => {
    returnRecord.items?.forEach(returnedItem => {
      const isMiscItem = item.Sku === '2';
      const matches = isMiscItem
        ? returnedItem.ItemCode === item.Sku && returnedItem.ItemName === item.Name
        : returnedItem.ItemCode === item.uniqueId;

      if (matches) {
        totalReturnedQty += Math.abs(returnedItem.Qty);
      }
    });
  });

  return item.Qty > totalReturnedQty;
};

// Get returned quantity for an item
const getReturnedQty = (item) => {
  if (!invoiceData.value.Returns || invoiceData.value.Returns.length === 0) return 0;

  let total = 0;
  invoiceData.value.Returns.forEach(returnRecord => {
    returnRecord.items?.forEach(returnedItem => {
      const isMiscItem = item.Sku === '2';
      const matches = isMiscItem
        ? returnedItem.ItemCode === item.Sku && returnedItem.ItemName === item.Name
        : returnedItem.ItemCode === item.uniqueId;

      if (matches) {
        total += Math.abs(returnedItem.Qty);
      }
    });
  });

  return total;
};

const taxRate = ref(0);

// Get tax rate from invoice data and add themm
const getTaxRate = () => {
  return (
    invoiceData.value.Invoice?.Taxes.forEach((tax) => {
      if (tax.TaxId) {
        taxRate.value += tax.Amount;
        console.log('Tax rate from order', taxRate.value);
      }
    })
  );
};

// Restocking Fee Logic
const restockingFeeVisibility = ref({});

const showRestockingFeeInput = (itemId) => {
  restockingFeeVisibility.value[itemId] = true;
};

const calculateTotalRestockingFee = computed(() => {
  return itemsToReturn.value.reduce((total, item) => {
    const restockingFee = parseFloat(item.restockingFee) || 0;
    return total + restockingFee;
  }, 0).toFixed(2);
});

const restockingFeeTaxBreakdown = computed(() => {
  const taxes = invoiceData.value.Invoice?.Taxes || [];
  const restockingFee = parseFloat(calculateTotalRestockingFee.value) || 0;

  return taxes.map(tax => {
    return {
      taxId: tax.TaxId,
      amount: parseFloat((restockingFee * (tax.Amount / 100)).toFixed(2))
    };
  });
});

const restockingFeeWithTax = computed(() => {
  const total = calculateTotalRestockingFee.value * (1 + taxRate.value / 100);
  return parseFloat(total.toFixed(2));
});

const calculateRestockingFeePercentage = (item) => {
  const percent = parseFloat(item.restockingFeePercent) || 0;
  if (percent < 0 || percent > 100) {
    toast.error('Restocking fee must be between 0 and 100%');
    item.restockingFeePercent = null;
    item.restockingFee = null;
    return;
  }

  const originalQty = originalQuantities.value[item.uniqueId] || 0;
  const currentQty = returnQuantities.value[item.uniqueId] || 0;
  const qtyToReturn = originalQty - currentQty;

  if (qtyToReturn > 0) {
    item.restockingFee = ((percent / 100) * item.Rate * qtyToReturn).toFixed(2);
  } else {
    item.restockingFee = 0;
  }
};

// Restocking Fee Logic End

// Calculate total to return
const calculatedTotalToReturn = computed(() => {
  if (!invoiceData.value.Invoice?.OrderItems) return 0;

  let subtotal = 0;
  let totalRestockingFee = 0;
  const overallDiscount = invoiceData.value.Invoice.OverallDiscount || 0;

  invoiceData.value.Invoice.OrderItems.forEach(item => {
    const originalQty = originalQuantities.value[item.uniqueId] || 0;
    const currentQty = returnQuantities.value[item.uniqueId] || 0;
    const qtyToReturn = originalQty - currentQty;

    if (qtyToReturn > 0) {
      const itemSubtotal = qtyToReturn * item.Rate;
      const restockingFee = parseFloat(item.restockingFee) || 0;
      subtotal += itemSubtotal;
      totalRestockingFee += restockingFee;
    }
  });

  // Apply overall discount
  if (overallDiscount > 0) {
    subtotal = subtotal * (1 - overallDiscount / 100);
  }

  // Deduct restocking fee BEFORE tax
  const subtotalAfterFees = subtotal - totalRestockingFee;

  // Tax based on reduced amount
  const totalTax = subtotalAfterFees * (taxRate.value / 100);

  return (subtotalAfterFees + totalTax).toFixed(2);
});


// Decrease item qty
function decreaseItem(item) {
  const currentQty = returnQuantities.value[item.uniqueId] || 0;
  if (currentQty > 0) {
    returnQuantities.value[item.uniqueId] = currentQty - 1;

    // Recalculate restocking fee if percentage is already set
    if (item.restockingFeePercent) {
      calculateRestockingFeePercentage(item);
    }
  }
}

// Enforce max value in input
function enforceMaxValue(item, event) {
  const max = originalQuantities.value[item.uniqueId];
  if (event.target.value > max) {
    returnQuantities.value[item.uniqueId] = max;
  }
  if (event.target.value < 0) {
    returnQuantities.value[item.uniqueId] = 0;
  }
}

const itemsToReturn = computed(() => {
  if (!invoiceData.value.Invoice?.OrderItems) return [];
  
  return invoiceData.value.Invoice.OrderItems.filter(item => {
    const originalQty = originalQuantities.value[item.uniqueId] || 0;
    const currentQty = returnQuantities.value[item.uniqueId] || 0;
    return (originalQty - currentQty) > 0;
  });
});

const paymentMethods = computed(() => {
  return invoiceData.value.Invoice?.Payment || [];
});

const getPaymentIcon = (method) => {
  const icons = {
    'Visa': '/visa.svg',
    'Master Card': '/mastercard.svg',
    'Cash': '/cash.svg',
    'Debit Card': '/debitCard.png',
    'Amex': '/american-express.svg',
    'Interac Etransfer': '/Interac.svg',
  };
  return icons[method] || '/images/default-payment-icon.png';
};

const isItemSelectedForReturn = computed (() => {
  return calculatedTotalToReturn.value > 0;
});

//Get current item price 
const getItemPrice = computed(() => (item) => {
      const overallDiscount = invoiceData.value.Invoice?.OverallDiscount || 0;
      return overallDiscount > 0 
        ? (item.Rate * (1 - overallDiscount / 100)).toFixed(2)
        : item.Rate.toFixed(2);
    });

// Check if commentText is empty
const isCommentTextEmpty = computed(() => {
  return !commentText.value || commentText.value.trim() === '';
});
const modeOfReturnId = computed(() => {
  const method = authStore.selectedRefundMethod;
  return authStore.modeMap[method] || 0;
});
// Prepare return payload
const prepareReturnPayload = () => {
  if (!invoiceData.value.Invoice?.OrderItems) return null;

  const overallDiscount = invoiceData.value.Invoice.OverallDiscount || 0;
  const totalRestockingFee = restockingFeeWithTax.value;

  const payload = {
    invoiceId: invoiceData.value.Invoice.InvoiceId,
    orderId: orderId.value,
    overallDiscount: overallDiscount,
    SalesPersonId: invoiceData.value.Invoice.SalesPersonId || "",
    total: calculatedTotalToReturn.value,
    approvalList: invoiceData.value.Invoice.ApprovalList?.map(approval => ({
      itemId: approval.itemId || "",
      managerApprovalId: approval.managerApprovalId || "",
      managerId: approval.managerId || "",
      cashierId: approval.cashierId || "",
      id: approval.id || "",
      isOverallDiscount: approval.isOverallDiscount || false
    })) || [],
    
    modeOfReturnId: modeOfReturnId.value,
    totalRestockingFee: totalRestockingFee,

    comments: commentText.value ? [{
      userId: authStore.managerUser || "",
      text: commentText.value
    }] : [],

    // comments: invoiceData.value.Invoice.Comments?.map(comment => ({
    //   userId: comment.userId || "",
    //   text: comment.text || ""
    // })) || [],
    itemList: [],
    payment: invoiceData.value.Invoice.Payment?.map(payment => ({
      paymentMethodId: 0,
      amount: payment.Amount || 0,
      percentage: 0,
      change: payment.Change || 0
    })) || [],
    taxes: invoiceData.value.Invoice.Taxes?.map(tax => ({
      taxId: tax.TaxId || "",
      amount: tax.Amount || 0
    })) || [],
    customer: {
      id: invoiceData.value.Invoice.Customer?.id || "",
      name: invoiceData.value.Invoice.Customer?.Name || "",
      phone: invoiceData.value.Invoice.Customer?.Phone || "",
      email: invoiceData.value.Invoice.Customer?.Email || "",
      note: invoiceData.value.Invoice.Customer?.Note || ""
    }
  };

  payload.itemList = invoiceData.value.Invoice.OrderItems
    .map(item => {
      const currentDisplayedQty = returnQuantities.value[item.uniqueId] || 0;
      const qtyToReturn = originalQuantities.value[item.uniqueId] - currentDisplayedQty;

      // Computed property to get item price after overall discount
      
      if (qtyToReturn > 0) {
        // Calculate discounted rate if there's an overall discount
        const discountedRate = overallDiscount > 0 
          ? item.Rate * (1 - overallDiscount / 100)
          : item.Rate;
        
        const discountRareForSingleItem = item.Discount > 0 ? item.Price : item.Rate;
        
        return {
          itemId: item.Sku,
          itemName: item.Name,
          qty: qtyToReturn,
          originalQty: item.Qty,
          rate: discountRareForSingleItem,
          //for overalldiscount
          discountedPrice: discountedRate,
          discount: item.Discount || 0,
          taxesWaived: false,
          sku: item.RetailSku,
          ImageUrl: item.ImageUrl,
          restockFee: (() => {
            const fee = parseFloat(item.restockingFee) || 0;
            const feeWithTax = fee + (fee * (taxRate.value / 100));
            return parseFloat(feeWithTax.toFixed(2));
          })(),
        };
      }
      return null;
    })
    .filter(item => item !== null);

  return payload;
};

//transform payload to match reprint object
const transformToReprintPayload = (payload, couponCode) => {
  if (!payload) return null;

  const invoiceItems = payload.itemList.map(item => ({
    name: item.itemName,
    quantity: item.qty,
    originalQty: item.originalQty,
    price: item.discountedPrice,
    total: item.qty * item.discountedPrice,
    originalPrice: item.rate,
    discount: item.discount,
    sku: item.sku,
  }));

  const subtotal = parseFloat(invoiceItems.reduce((acc, item) => acc + parseFloat(item.total), 0).toFixed(2));

  const taxes = payload.taxes.map(tax => ({
    type: tax.taxId || 'Tax',
    rate: 0,
    amount: Math.abs(tax.amount)
  }));

  //get the first Item image
  const firstItemImage = payload.itemList.length > 0 ? payload.itemList[0].ImageUrl : null;

  const gst = Math.abs(payload.taxes[0]?.amount || 0.00);
  const pst = Math.abs(payload.taxes[1]?.amount || 0.00);

  const calcTax1 = ((subtotal * gst) / 100).toFixed(2);
  const calcTax2 = ((subtotal * pst) / 100).toFixed(2);

  return {
    Salesperson: payload.SalesPersonId || null,
    invoiceId: `SAL-ORD-${payload.orderId}`,
    customerName: payload.customer?.name,
    date: Date.now(),
    gst: calcTax1,
    pst: calcTax2,
    subtotal: subtotal,
    previewImage: firstItemImage,
    totalAmount: payload.total,
    changeAmount: payload.payment.reduce((sum, p) => sum + (p.change || 0), 0),
    remainingStoreCredit: "0.00",
    isThisReturnReciept: true,
    taxes: taxes,
    items: invoiceItems,
    couponCodeForReprint: couponCode,
    restockingFeeTotal: payload.totalRestockingFee,
    isThisCashReturn: authStore.selectedRefundMethod === 'Cash' ? true : false,
  };
};



const isGetCouponCodeLoading = ref(false);

// Get coupon code request
const getCouponCode = async () => {
  if (isGetCouponCodeLoading.value) return;
  
  const returnPayload = prepareReturnPayload();
  
  const noItemsSelected = invoiceData.value.Invoice?.OrderItems?.every(
    item => returnQuantities.value[item.uniqueId] === originalQuantities.value[item.uniqueId]
  );
  
  if (!returnPayload || noItemsSelected) {
    toast.error('No items selected for return');
    return;
  }

  isGetCouponCodeLoading.value = true;
  try {
    const response = await axios.post('/api/Order/SalesReturn',returnPayload, 
      {
        headers: {
          'Authorization': `Bearer ${authStore.managerToken ? authStore.managerToken : authStore.token}`,
          'store-id': authStore.branchId
        }
      }
    );

    if (response.data.status === 200 && Math.abs(
    Math.abs(parseFloat(calculatedTotalToReturn.value)) - Math.abs(response.data.total)
  ) <= .25) {

      toast.success('Coupon generated successfully');
      setCouponCode(response.data.couponCode);
      console.log('Coupon response:', response.data);
      const reprintPayload = transformToReprintPayload(returnPayload, response.data.couponCode);
      setReturnPrint(returnPayload);
      orderStore.saveReceiptToStorage(reprintPayload);
      console.log('Return new receipt:', orderStore.state.returnPrint);
      authStore.toggleRefundPopup();
      authStore.toggleCheckoutPopup();
    } else {
      toast.error(response.data.message || 'Failed to generate coupon');
    }
  } catch (err) {
    error.value = err.response?.data?.Message || err.message;
    toast.error('Error generating coupon');
    console.error('API Error:', err);
  } finally {
    isGetCouponCodeLoading.value = false;
  }
};

const isSaleOrderLoaded = computed(() => {
  return invoiceData.value.Invoice?.OrderItems && invoiceData.value.Invoice?.OrderItems.length > 0;
});

// For Return Print start
const setReturnPrint = (receipt) => {
  const invoiceItems = receipt.itemList.map(item => ({
    name: item.itemName,
    quantity: item.qty,
    price: invoiceData.value.Invoice?.OverallDiscount > 0 || item.discount > 0 ? item.discountedPrice : parseFloat(item.rate),
    total: invoiceData.value.Invoice?.OverallDiscount > 0 || item.discount > 0 ? parseFloat(item.qty * item.discountedPrice) : parseFloat(item.qty * parseFloat(item.rate)),
    originalPrice: parseFloat(item.rate), 
    discount: item.discount,
    sku: item.sku,
    orgQty: item.originalQty,
  }));
  
  // Calculate subtotal for 
  const subtotal = parseFloat(invoiceItems.reduce((acc, item) => acc + parseFloat(item.total), 0).toFixed(2));

  const taxes = receipt.taxes.map(tax => ({
    type: tax.taxId || 'Tax', 
    rate: 0, 
    amount: Math.abs(tax.amount)
  }));

  const totalRestockingFee = receipt.totalRestockingFee || 0;

  const customerName = receipt.customer?.name;

  const gst = Math.abs(receipt.taxes[0]?.amount || 0.00);
  const pst = Math.abs(receipt.taxes[1]?.amount || 0.00);

  // Calculate taxe using the first tax rete
  const calcTax1 = ((subtotal * gst) / 100).toFixed(2);
  const calcTax2 = ((subtotal * pst) / 100).toFixed(2);
  
  orderStore.state.returnPrint = {
    items: invoiceItems,
    orderId: receipt.orderId,
    invoiceId: receipt.invoiceId,
    subtotal: subtotal,
    discountAmount: receipt.overallDiscount || 0,
    taxes: taxes,
    totalAmount: receipt.total,
    date: Date.now(),
    gst: calcTax1,
    pst: calcTax2,
    customer: customerName,
    totalRestockingFee: totalRestockingFee,
  };
}
// For Return Print end

const setCouponCode = (couponCode) => {
  orderStore.state.couponCode = couponCode;
};

// Reset selecteed items to return
const resetReturn = () => {
  itemsToReturn.value.forEach(item => {
    returnQuantities.value[item.uniqueId] = originalQuantities.value[item.uniqueId];
    item.restockingFeePercent = null;
    item.restockingFee = null;
  });
  commentText.value = null;
}

function handleToggle () {
  authStore.toggleRefundPopup();
  authStore.toggleManagerLoginPopup();
}

</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div 
        class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8">
        <Transition name="modal-inner" class="rounded-2xl">
          <div
            class="fixed top-10 z-50 flex items-center justify-center bg-black bg-opacity-50 w-10/12">
            <div class="bg-white rounded-2xl shadow-lg p-6 w-full h-[90vh] overflow-y-auto">
              <i @click="authStore.toggleRefundPopup"
              class="class-hide-on-print pi pi-times-circle w-full text-right absolute top-3 right-3 cursor-pointer"
              :disabled="isLoading" :class="{ 'opacity-50 cursor-not-allowed': isLoading }" style="font-size: 24px;"></i>
              <h1 class="font-semibold text-[24px] pb-4">Returns</h1>

              <!-- Search Section -->
              <div class="flex h-[75vh] gap-4">
                <div class="w-1/2 flex flex-col justify-between">
                  <div class="top">
                    <div class="bg-gray-100 rounded-2xl px-2 py-1 flex items-center gap-2">
                      <label for="receipt-id">SAL-ORD-</label>
                      <div class="p-1 rounded-lg flex-1" v-if="!isSearchSubmitted">
                        <input 
                          type="text"
                          class="p-1 border-none outline-none focus:outline-none w-full bg-gray-100" 
                          placeholder="Enter Order ID" 
                          v-model="orderId"
                          @keydown.enter="handleKeyDown"
                        />
                      </div>
                      <p v-if="isSearchSubmitted">{{ orderId.startsWith('SAL-ORD-') ? orderId.slice(8) : orderId }}</p>
                      <button
                        @click="fetchInvoice"
                        :disabled="isLoading || isSaleOrderLoaded"
                        class="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md transition-colors ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span v-if="!isLoading">Load Order</span>
                        <span v-else>Loading...</span>
                      </button>
                    </div>


                    <div class="bottom flex flex-col gap-4 mt-4">
                      <div v-if="itemsToReturn.length > 0" class="relative items-to-return bg-gray-100 p-3 mt-4 rounded-lg">
                        <h3 class="font-semibold mb-4">Items to Return</h3>
                        <button  @click="resetReturn" class="absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                         :disabled="isGetCouponCodeLoading || !isSaleOrderLoaded"
                        >
                          <i class="pi pi-refresh"></i>
                          Reset
                        </button>
                        
                        <div class="flex flex-wrap gap-2 w-full max-h-48 overflow-y-auto">
                          <div 
                            v-for="item in itemsToReturn" 
                            :key="item.uniqueId"
                            class="relative flex flex-col items-start justify-between gap-1 bg-white p-2 rounded-lg shadow-sm w-full max-h-20"
                          >
                            <div class="flex items-center justify-between w-full gap-2">
                              <div class="flex items-center gap-2">
                                <img 
                                  :src="item.ImageUrl" 
                                  :alt="`Image of ${item.Name}`" 
                                  class="w-8 h-8 object-cover"
                                  v-if="item.ImageUrl"  
                                >
                                <div v-else class="w-8 h-8 bg-gray-200 flex items-center justify-center">
                                  <span class="text-gray-500 text-xs">No img</span>
                                </div>
                                <span class="text-sm truncate max-w-[500px]">{{ item.Name }}</span>
                                <span class="text-sm absolute right-0 top-0 p-2 bg-purple-200 rounded-full h-5 w-5 flex items-center justify-center">
                                  {{ originalQuantities[item.uniqueId] - returnQuantities[item.uniqueId] }}
                                </span>
                              </div>
                              <div class="restocking-fee-div flex items-center gap-2 align-self-end mr-8">
                                <button 
                                  @click="showRestockingFeeInput(item.uniqueId)" 
                                  class="flex items-center gap-2 bg-purple-200 text-white px-2 py-1 rounded-md hover:bg-purple-600 transition-colors"
                                >
                                  <span class="pi pi-plus"></span>
                                  <span class="">Add Restocking Fee</span>
                                </button>
                              </div>
                            </div>
                            <div v-if="restockingFeeVisibility[item.uniqueId]" class="flex items-center gap-2">
                              <span class="pi pi-percentage"></span>
                              <input
                                type="number"
                                :class="[
                                  'text-center rounded p-1 border-none outline-none bg-gray-100 transition-all duration-200 ease-in-out',
                                  item.restockingFeePercent
                                    ? 'w-16'
                                    : 'w-48 focus:w-16'
                                ]"
                                placeholder="Add Restocking Fee %"
                                v-model="item.restockingFeePercent"
                                @input="calculateRestockingFeePercentage(item)"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="isItemSelectedForReturn && authStore.isManagerLoggedIn" class="mode-of-paymentt flex flex-col gap-3 mb-5 p-3 bg-gray-100 rounded-xl">
                        <h3 class="font-semibold">Original Mode Of Payment</h3>
                        <div class="max-h-64 overflow-y-auto">
                          <div v-for="(method, index) in paymentMethods" :key="index"
                            class="h-28 px-3 my-3 py-2 rounded-xl bg-white flex items-center justify-between text-center"
                          >
                            <div class="">
                              <img :src="getPaymentIcon(method.ModeOfPayment)" :alt="method.ModeOfPayment" class="w-12 h-12 mb-1" />
                              <span class="text-[11px] leading-snug whitespace-normal break-words">
                                {{ method.ModeOfPayment }}
                              </span>
                            </div>
                            <p class="">${{ method.Amount.toFixed(2) }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- Refund comment -->
                      <div v-if="isItemSelectedForReturn && authStore.isManagerLoggedIn" class="refund-comment p-4 bg-gray-100 rounded-lg">
                          <!--<div v-show="authStore.isManagerLoggedIn" class="refund-comment py-4"> -->
                          <h3 class="font-semibold mb-6">Refund Approval Comment</h3>
                          <div class="card flex flex-wrap justify-center items-end gap-4">
                            <FloatLabel variant="on" class="w-full px-4 py-3 bg-white rounded-lg ">
                                <InputText id="on_label" v-model="commentText" autocomplete="off" class="w-full"/>
                                <label for="on_label">Manager comment...</label>
                            </FloatLabel>
                          </div>
                      </div>
                      </div>
                  </div>

                  <div class="bottom ml-auto flex gap-2">
                    <!-- <button 
                      class="bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="!authStore.isManagerLoggedIn || isGetCouponCodeLoading || !isSaleOrderLoaded || itemsToReturn.length === 0 || isCommentTextEmpty"
                    >
                      OPM
                    </button> -->
                    <button 
                      @click="authStore.selectedRefundMethod = 'Cash'; getCouponCode()"
                      class="bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="!authStore.isManagerLoggedIn || isGetCouponCodeLoading || !isSaleOrderLoaded || itemsToReturn.length === 0 || isCommentTextEmpty"
                    >
                      Cash
                    </button>
                    <button 
                      @click="authStore.selectedRefundMethod = 'StoreCredit'; getCouponCode()"
                      :disabled="(isGetCouponCodeLoading || !isSaleOrderLoaded) || itemsToReturn.length === 0 || !isCommentTextEmpty"
                      class="bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span v-if="!isGetCouponCodeLoading">Store Credit</span>
                      <span v-else>Processing...</span>
                    </button>
                  </div>
                </div>
                <div class="w-1/2 flex flex-col justify-between">
                  <div class="right-top">
                    <div class="w-[95%] mx-auto flex flex-col gap-2 max-h-[550px] overflow-y-auto">
                      <div 
                        v-for="item in invoiceData.Invoice?.OrderItems" 
                        :key="item.uniqueId" 
                        class="item flex justify-between items-center bg-gray-100 rounded-2xl px-2 py-1 relative"
                        :class="{ 'opacity-50 border-2 border-red-500': !alredyReturnedItems(item) }"
                      >
                        <div v-if="!alredyReturnedItems(item)" class="absolute top-0 right-0 bg-red-500 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg">
                          All returned
                        </div>
                        <img 
                          :src="item.ImageUrl" 
                          :alt="`Image of ${item.Name}`" 
                          class="w-16 h-16 object-cover"
                          v-if="item.ImageUrl"  
                        >
                        <div v-else class="w-16 h-16 bg-gray-200 flex items-center justify-center">
                          <span class="text-gray-500">No image</span>
                        </div>
                        <div class="flex flex-col gap-1 w-80">
                          <p class="truncate max-w-[200px]">{{ item.Name }}</p>
                          <label>Sku: {{ item.RetailSku }}</label>
                        </div>
                        <div class="flex items-center gap-2">
                          <input 
                            type="number" 
                            class="text-center no-arrows border-2 rounded-lg py-1 w-12"
                            :min="0"
                            :max="originalQuantities[item.uniqueId]"
                            v-model="returnQuantities[item.uniqueId]"
                            :disabled="!alredyReturnedItems(item) || isGetCouponCodeLoading"
                            @input="enforceMaxValue(item, $event)"
                          />
                          <button class="pi pi-plus p-button-rounded p-2 font-extrabold hover:bg-purple-100 hover:rounded-lg"
                            style="font-size: 20px;" 
                            @click="decreaseItem(item)"
                            :disabled="!alredyReturnedItems(item) || isGetCouponCodeLoading"
                          />
                        </div>
                        <!-- <label v-if="invoiceData.value.Invoice?.OverallDiscount > 0">${{ item.discountedPrice }}</label> -->
                        <label>${{ getItemPrice(item) }}</label>
                      </div>
                    </div>
                  </div>

                  <div class="right-bottom">
                    <div>
                      <div class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3">
                        <p>Original Total Paid</p>
                        <p>${{ invoiceData.Invoice?.Total?.toFixed(2) || '0.00' }}</p>
                      </div>
                      <div v-if="calculateTotalRestockingFee > 0" class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3">
                        <p>Restocking Fee</p>
                        <p>${{ calculateTotalRestockingFee }}</p>
                      </div>
                      <div v-if="calculateTotalRestockingFee > 0" class="">
                        <div
                          v-for="tax in restockingFeeTaxBreakdown"
                          :key="tax.taxId"
                          class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3"
                        >
                          <p>Restocking Fee Tax ({{ tax.taxId }})</p>
                          <p>${{ tax.amount.toFixed(2) }}</p>
                        </div>
                      </div>
                      <div v-if="calculateTotalRestockingFee > 0" class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3">
                        <p>Total Restocking Fee With Tax</p>
                        <p>${{ restockingFeeWithTax.toFixed(2) }}</p>
                      </div>
                      <div class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 mt-2 py-4 px-3">
                        <p>Total Refund</p>
                        <p>${{ calculatedTotalToReturn }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p v-if="!authStore.isManagerLoggedIn" class="absolute bottom-6">Ask a manager for a <span class="text-purple-500 cursor-pointer font-bold" @click="handleToggle">Cash refund</span>.</p>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

/* Remove arrows from input element */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

/* Additional style for disabled items */
.item {
  position: relative;
  transition: all 0.3s ease;
}

.item.disabled {
  opacity: 0.6;
  background-color: #f8f9fa;
  border-left: 4px solid #dc3545;
}

/* Payment */
.mode-of-paymentt {
  margin-top: 1rem;
  flex-wrap: wrap;
}

.mode-of-paymentt > div {
  flex: 1;
  transition: all 0.2s ease;
}

.mode-of-paymentt img {
  object-fit: contain;
}
</style>